import {
  DELOAD_EVERY_WEEKS_MAX,
  DELOAD_EVERY_WEEKS_MIN,
  safeValidatePlanTemplate,
} from '../../validation/template.schemas';
import { formatDisplayDate } from '../date';
import {
  createExerciseFaultCollector,
  toFormExercise,
  toSetField,
  WORKOUT_FORM_DEFAULTS,
} from '../workout/workout-form';

import type {
  ImportableWorkoutItem,
  PastWorkoutItem,
} from '../../types/api/template';
import type { DifficultyLevel } from '../../validation/common.schemas';
import type {
  PlanTemplateOutput,
  TemplateExerciseOutput,
  TemplateWorkoutOutput,
} from '../../validation/template.schemas';
import type { WorkoutExerciseProblem } from '../workout/workout-form';

/**
 * Plan Form Model
 *
 * The plan form's state shape, its workout operations, the "add workout" step's
 * rules and its validation, as pure functions — shared by the web hook
 * (`usePlanForm`) and the mobile form so both edit a plan by the same rules.
 * Nothing here knows about React, storage or the network.
 *
 * A workout's own exercises are edited with the workout form's operations
 * (`utils/workout/workout-form`) and the group operations
 * (`utils/exercise-groups`), applied to one workout through
 * {@link updateWorkoutExercises}.
 */

// =============================================================================
// STATE
// =============================================================================

/** The fields the plan form edits. */
export interface PlanFormState {
  name: string;
  description: string;
  difficulty_level: DifficultyLevel;
  active: boolean;
  /** Every Nth week is a deload; `null` when none is scheduled. */
  deload_every_weeks: number | null;
  workouts: TemplateWorkoutOutput[];
}

/** The form's defaults — an intermediate plan, not active, with no deload. */
export const PLAN_FORM_DEFAULTS: PlanFormState = {
  name: '',
  description: '',
  difficulty_level: 'intermediate',
  active: false,
  deload_every_weeks: null,
  workouts: [],
};

/** A workout as the form holds it: its exercises as the workout form does. */
function toFormWorkout(workout: TemplateWorkoutOutput): TemplateWorkoutOutput {
  return workout.exercises
    ? { ...workout, exercises: workout.exercises.map(toFormExercise) }
    : workout;
}

/** The form's starting state: a draft or a saved plan over the defaults. */
export function createPlanFormState(
  initial?: Partial<PlanTemplateOutput>
): PlanFormState {
  return {
    name: initial?.name ?? PLAN_FORM_DEFAULTS.name,
    description: initial?.description ?? PLAN_FORM_DEFAULTS.description,
    difficulty_level:
      initial?.difficulty_level ?? PLAN_FORM_DEFAULTS.difficulty_level,
    active: initial?.active ?? PLAN_FORM_DEFAULTS.active,
    deload_every_weeks:
      initial?.deload_every_weeks ?? PLAN_FORM_DEFAULTS.deload_every_weeks,
    workouts:
      initial?.workouts?.map(toFormWorkout) ?? PLAN_FORM_DEFAULTS.workouts,
  };
}

/** Which plan the form is editing: a saved one's ids, or a new one's uuid. */
export interface PlanIdentity {
  id?: string | null | undefined;
  uuid: string;
}

/** The payload the form saves — and the shape a draft is stored in. */
export function toPlanTemplate(
  state: PlanFormState,
  identity: PlanIdentity
): PlanTemplateOutput {
  return {
    id: identity.id,
    uuid: identity.uuid,
    name: state.name,
    description: state.description,
    difficulty_level: state.difficulty_level,
    active: state.active,
    deload_every_weeks: state.deload_every_weeks,
    workouts: state.workouts,
  };
}

// =============================================================================
// WORKOUT OPERATIONS
// =============================================================================

/** Appends a workout to the end of the plan. */
export function addWorkout(
  workouts: TemplateWorkoutOutput[],
  workout: TemplateWorkoutOutput
): TemplateWorkoutOutput[] {
  return [...workouts, workout];
}

/** Changes some of one workout's fields. */
export function updateWorkout(
  workouts: TemplateWorkoutOutput[],
  workoutUuid: string,
  change: Partial<TemplateWorkoutOutput>
): TemplateWorkoutOutput[] {
  return workouts.map((w) =>
    w.uuid === workoutUuid ? { ...w, ...change } : w
  );
}

/** Removes one workout. */
export function removeWorkout(
  workouts: TemplateWorkoutOutput[],
  workoutUuid: string
): TemplateWorkoutOutput[] {
  return workouts.filter((w) => w.uuid !== workoutUuid);
}

/**
 * Puts the workouts in the given order. A uuid that matches no workout is
 * skipped, and a workout left out of the order is dropped.
 */
export function reorderWorkouts(
  workouts: TemplateWorkoutOutput[],
  workoutUuids: string[]
): TemplateWorkoutOutput[] {
  return workoutUuids
    .map((uuid) => workouts.find((w) => w.uuid === uuid))
    .filter((w): w is TemplateWorkoutOutput => w !== undefined);
}

/**
 * Applies an exercise operation to one workout's exercises. A workout with no
 * exercises yet is operated on as an empty list. A workout whose exercises come
 * back unchanged — the same array, or still none — is left as it is, so a no-op
 * does not look like an edit.
 */
export function updateWorkoutExercises(
  workouts: TemplateWorkoutOutput[],
  workoutUuid: string,
  update: (exercises: TemplateExerciseOutput[]) => TemplateExerciseOutput[]
): TemplateWorkoutOutput[] {
  return workouts.map((w) => {
    if (w.uuid !== workoutUuid) return w;
    const current = w.exercises ?? [];
    const exercises = update(current);
    const unchanged = w.exercises
      ? exercises === current
      : exercises.length === 0;
    return unchanged ? w : { ...w, exercises };
  });
}

// =============================================================================
// ADD / EDIT A WORKOUT
// =============================================================================

/** What the "new workout" and "edit workout" steps ask for. */
export interface PlanWorkoutDetails {
  name: string;
  description: string;
  difficulty_level: DifficultyLevel;
  rest_duration: number;
}

/**
 * The details step's starting values: the workout being edited, or a new one
 * that takes the plan's difficulty and the standard rest.
 */
export function createPlanWorkoutDetails(
  planDifficulty: DifficultyLevel,
  workout?: TemplateWorkoutOutput
): PlanWorkoutDetails {
  return {
    name: workout?.name ?? '',
    description: workout?.description ?? '',
    difficulty_level: workout?.difficulty_level ?? planDifficulty,
    rest_duration:
      workout?.rest_duration ?? WORKOUT_FORM_DEFAULTS.rest_duration,
  };
}

/** A workout needs a name before it can be created or saved. */
export function canSubmitPlanWorkout(details: PlanWorkoutDetails): boolean {
  return details.name.trim() !== '';
}

/**
 * The workout the details step produces. Editing keeps the workout's identity
 * and exercises; a new one starts empty under a fresh uuid.
 */
export function buildPlanWorkout(
  details: PlanWorkoutDetails,
  createId: () => string,
  existing?: TemplateWorkoutOutput
): TemplateWorkoutOutput {
  return {
    uuid: existing ? existing.uuid : createId(),
    id: existing ? existing.id : null,
    name: details.name.trim(),
    description: details.description.trim() || null,
    difficulty_level: details.difficulty_level,
    rest_duration: details.rest_duration,
    exercises: existing ? existing.exercises : [],
  };
}

/** The fields an edit of a workout's details changes — never its exercises. */
export function toPlanWorkoutDetailsChange(
  workout: TemplateWorkoutOutput
): Partial<TemplateWorkoutOutput> {
  return {
    name: workout.name,
    description: workout.description,
    difficulty_level: workout.difficulty_level,
    rest_duration: workout.rest_duration,
  };
}

/**
 * A saved workout brought into a plan: a copy under a new uuid, with its own
 * id and its exercises' ids dropped, so saving the plan creates new records
 * rather than moving the originals. Its exercises are read as the form holds
 * them (`toFormExercise`), like the plan's own: as the API sends them, an
 * ungrouped exercise's `group_type: null` and a set's decimal-text duration
 * fail the plan's validation.
 */
export function toImportedWorkout(
  template: Omit<TemplateWorkoutOutput, 'id' | 'uuid'>,
  createId: () => string
): TemplateWorkoutOutput {
  return {
    uuid: createId(),
    id: null,
    name: template.name,
    description: template.description,
    difficulty_level: template.difficulty_level,
    rest_duration: template.rest_duration,
    exercises: (template.exercises ?? []).map((exercise) => ({
      ...toFormExercise(exercise),
      id: null,
    })),
  };
}

/**
 * Brings saved workouts into a plan: reads each one in full and copies it (see
 * {@link toImportedWorkout}), in the order they were asked for. Rejects if any
 * cannot be read, so a plan never gets half of what was picked.
 */
export async function importWorkouts(
  workoutIds: string[],
  loadTemplate: (
    workoutId: string
  ) => Promise<Omit<TemplateWorkoutOutput, 'id' | 'uuid'>>,
  createId: () => string
): Promise<TemplateWorkoutOutput[]> {
  const templates = await Promise.all(workoutIds.map(loadTemplate));
  return templates.map((template) => toImportedWorkout(template, createId));
}

export const IMPORT_ERROR = 'Failed to import workouts. Please try again.';

/** The import list's two sources. */
export type ImportTab = 'library' | 'past';

export const IMPORT_TABS: readonly { id: ImportTab; label: string }[] = [
  { id: 'library', label: 'LIBRARY' },
  { id: 'past', label: 'PAST WORKOUTS' },
];

/** "Search library..." / "Search past workouts..." */
export function getImportSearchPlaceholder(tab: ImportTab): string {
  return `Search ${tab === 'library' ? 'library' : 'past workouts'}...`;
}

/** The workouts whose name contains the query, ignoring case. */
export function filterImportableWorkouts<T extends ImportableWorkoutItem>(
  workouts: T[],
  query: string
): T[] {
  if (!query) return workouts;
  const lower = query.toLowerCase();
  return workouts.filter((workout) =>
    workout.name.toLowerCase().includes(lower)
  );
}

/**
 * The line under an importable workout's name: when a past workout was done
 * and how many exercises it has, or a library workout's count and preview.
 */
export function getImportableWorkoutSubtitle(
  workout: ImportableWorkoutItem | PastWorkoutItem
): string {
  if ('completed_at' in workout) {
    return [
      formatDisplayDate(workout.completed_at, 'recent'),
      `${workout.exercise_count} EXERCISES`,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  return `${workout.exercise_count} EXERCISES - ${workout.exercise_preview}`;
}

// =============================================================================
// DELOAD CADENCE
// =============================================================================

/** `''` stands for `null` — a select has no null value. */
export const NO_DELOAD = '';

/** The deload select's options: none, then every cadence the API accepts. */
export const DELOAD_OPTIONS: readonly { value: string; label: string }[] = [
  { value: NO_DELOAD, label: 'No scheduled deload' },
  ...Array.from(
    { length: DELOAD_EVERY_WEEKS_MAX - DELOAD_EVERY_WEEKS_MIN + 1 },
    (_, index) => {
      const weeks = DELOAD_EVERY_WEEKS_MIN + index;
      return { value: String(weeks), label: `Every ${weeks} weeks` };
    }
  ),
];

export const DELOAD_HELP_TEXT =
  'A deliberately easier week — same lifts, far fewer sets.';

/** A cadence as the select's value. */
export function toDeloadOptionValue(weeks: number | null): string {
  return weeks === null ? NO_DELOAD : String(weeks);
}

/** The select's value as a cadence. */
export function fromDeloadOptionValue(value: string): number | null {
  return value === NO_DELOAD ? null : Number(value);
}

// =============================================================================
// VALIDATION
// =============================================================================

/** One message per top-level field — what a form shows under each. */
export interface PlanValidationErrors {
  name?: string;
  description?: string;
  difficulty_level?: string;
  active?: string;
  deload_every_weeks?: string;
  workouts?: string;
}

/** A workout that cannot be saved as it is. */
export interface PlanWorkoutProblem {
  workoutUuid: string;
  workoutName: string;
  /** What is wrong with the workout's own fields (its name, its rest). */
  messages: string[];
  /** The exercises whose sets have a value missing or not valid. */
  exerciseProblems: WorkoutExerciseProblem[];
}

export interface PlanFormValidation {
  valid: boolean;
  errors: PlanValidationErrors;
  /** The workouts behind `errors.workouts`, for marking them in a list. */
  workoutProblems: PlanWorkoutProblem[];
}

const UNTITLED_WORKOUT = 'Untitled Workout';

/**
 * What to tell the user when workouts block the save: each workout by name, in
 * quotes, with what is wrong in it — its own fields first, then its exercises
 * with the values they are missing.
 */
export function describePlanWorkoutProblems(
  problems: PlanWorkoutProblem[]
): string {
  const list = problems
    .map((problem) => {
      const parts = [
        ...problem.messages,
        ...problem.exerciseProblems.map(
          (p) => `“${p.exerciseName}” (${p.fields.join(', ')})`
        ),
      ];
      return `“${problem.workoutName || UNTITLED_WORKOUT}”: ${parts.join(', ')}`;
    })
    .join('; ');
  const lead =
    problems.length === 1
      ? 'This workout cannot be saved yet'
      : 'These workouts cannot be saved yet';
  return `${lead} — ${list}.`;
}

/**
 * Validates the form: the shared plan schema, then every set of every workout
 * against its exercise's tracking type (see `validateWorkoutForm`, whose set
 * checks these are).
 *
 * Top-level field errors are the schema's own messages, the last issue per
 * field winning. Everything under `workouts` is gathered per workout and
 * described in one sentence, because the schema's messages name neither the
 * workout nor the exercise they are about.
 */
export function validatePlanForm(
  state: PlanFormState,
  identity: PlanIdentity
): PlanFormValidation {
  const result = safeValidatePlanTemplate(toPlanTemplate(state, identity));

  const errors: PlanValidationErrors = {};
  const perWorkout = state.workouts.map((workout) => ({
    workout,
    messages: [] as string[],
    faults: createExerciseFaultCollector(),
  }));

  for (const issue of result.success ? [] : result.error.issues) {
    const [field, workoutIndex, part, exerciseIndex, sub, setIndex, setField] =
      issue.path;
    if (typeof field !== 'string') continue;

    const entry =
      field === 'workouts' && typeof workoutIndex === 'number'
        ? perWorkout[workoutIndex]
        : undefined;

    if (!entry) {
      errors[field as keyof PlanValidationErrors] = issue.message;
      continue;
    }

    const exercise =
      part === 'exercises' && typeof exerciseIndex === 'number'
        ? entry.workout.exercises?.[exerciseIndex]
        : undefined;

    if (exercise && sub === 'sets' && typeof setIndex === 'number') {
      entry.faults.add(exercise.uuid, setIndex + 1, toSetField(setField));
    } else if (!entry.messages.includes(issue.message)) {
      entry.messages.push(issue.message);
    }
  }

  const workoutProblems: PlanWorkoutProblem[] = perWorkout
    .map(({ workout, messages, faults }) => {
      const exercises = workout.exercises ?? [];
      faults.addMissingValues(exercises);
      return {
        workoutUuid: workout.uuid,
        workoutName: workout.name,
        messages,
        exerciseProblems: faults.toProblems(exercises),
      };
    })
    .filter(
      (problem) =>
        problem.messages.length > 0 || problem.exerciseProblems.length > 0
    );

  if (workoutProblems.length > 0) {
    errors.workouts = describePlanWorkoutProblems(workoutProblems);
  }

  return {
    valid: result.success && workoutProblems.length === 0,
    errors,
    workoutProblems,
  };
}

/** Whether the form can be saved as it stands. */
export function isPlanFormValid(
  state: PlanFormState,
  identity: PlanIdentity
): boolean {
  return validatePlanForm(state, identity).valid;
}
